import React, { useState, useEffect, useContext } from "react";
import {
  Search,
  MapPin,
  Briefcase,
  Clock,
  Bookmark,
  Filter,
  RotateCcw,
  ArrowRight,
  Users,
  Home,
  BookOpen,
  Heart,
  ChefHat,
  AlertTriangle,
  Lock,
  ChevronDown,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import Pagination from "../components/Pagination";
import { CareGiverContext } from "../context/CareGiverContext";
import JobDetailModal from "../components/JobDetailModal";
import NotOnboardedMessage from "../components/NotOnboardedMessage";
import {
  experienceOptions,
  SUBCITIES,
  workTypeOptions,
} from "../utils/constants";
import { formatExperienceDisplay } from "../utils/formatters";

const CaregiverRequestList = () => {
  const { t } = useTranslation();
  const {
    jobs,
    applyToJob,
    profileData,
    hasActiveWork,
    notOnboarded,
    getJobs,
    jobPagination,
    loading,
    getWorkStateLabel,
    getWorkStateColor,
    cToken,
  } = useContext(CareGiverContext);

  const [enhancedJobs, setEnhancedJobs] = useState([]);
  const [mainSearch, setMainSearch] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [selectedExperience, setSelectedExperience] = useState("");
  const [subcityFilter, setSubcityFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [bookmarkedJobs, setBookmarkedJobs] = useState(new Set());
  const [selectedJob, setSelectedJob] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [showSubcityDropdown, setShowSubcityDropdown] = useState(false);
  const [showWorkTypeDropdown, setShowWorkTypeDropdown] = useState(false);
  const [showExperienceDropdown, setShowExperienceDropdown] = useState(false);

  // Category icons mapping
  const categoryIcons = {
    childcare: Users,
    cook: ChefHat,
    cleaner: Home,
    homeTutor: BookOpen,
    elderlycare: Heart,
  };

  // Category labels using i18n
  const getCategoryLabel = (category) => {
    const map = {
      childcare: t("requests.categoryChildCare"),
      cook: t("requests.categoryCook"),
      cleaner: t("requests.categoryCleaner"),
      homeTutor: t("requests.categoryHomeTutor"),
      elderlycare: t("requests.categoryElderlyCare"),
    };
    return map[category] || t("requests.categoryGeneral");
  };

  // Helper function to truncate description for card display
  const getShortDescription = (description) => {
    if (!description) return t("requests.noDescription");
    return description.length > 70
      ? description.substring(0, 70) + "..."
      : description;
  };

  const getJobType = (job) => {
    const typeMap = {
      part_time: t("requests.workTimePartTime"),
      on_demand: t("requests.workTimeOnDemand"),
      living_in: t("requests.workTimeLivingIn"),
      night_shift: t("requests.workTimeNightShift"),
    };
    return typeMap[job.workTime] || t("requests.workTimeFull");
  };

  const formatPostedDate = (dateString) => {
    if (!dateString) return t("requests.postedRecently");
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return t("requests.postedToday");
    if (diffDays === 1) return t("requests.postedYesterday");
    if (diffDays < 7) return t("requests.postedDaysAgo", { count: diffDays });
    if (diffDays < 30)
      return t("requests.postedWeeksAgo", {
        count: Math.floor(diffDays / 7),
      });
    return t("requests.postedMonthsAgo", {
      count: Math.floor(diffDays / 30),
    });
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".dropdown-container")) {
        setShowSubcityDropdown(false);
        setShowWorkTypeDropdown(false);
        setShowExperienceDropdown(false);
      }
    };

    if (showSubcityDropdown || showWorkTypeDropdown || showExperienceDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showSubcityDropdown, showWorkTypeDropdown, showExperienceDropdown]);

  useEffect(() => {
    if (!cToken) return;

    const fetchParams = {
      page: currentPage,
      limit: itemsPerPage,
      search: searchQuery,
      subcity: subcityFilter,
      workTime: selectedType,
      experienceLevel: selectedExperience,
    };

    getJobs(fetchParams);
  }, [
    cToken,
    currentPage,
    itemsPerPage,
    searchQuery,
    subcityFilter,
    selectedType,
    selectedExperience,
  ]);

  useEffect(() => {
    if (jobs) {
      const enhanced = jobs.map((job) => {
        // Construct experienceLevel from experienceMin and experienceMax
        let experienceLevel = "";
        if (job.experienceMin && job.experienceMax) {
          experienceLevel = `${job.experienceMin}-${job.experienceMax}`;
        } else if (job.experienceMin) {
          experienceLevel = `${job.experienceMin}+`;
        }

        return {
          id: job.id,
          title: getShortDescription(job.description),
          category: job.category,
          categoryLabel: getCategoryLabel(job.category),
          type: getJobType(job),
          location: `${job.subcity || "Unknown"}, ${job.location || "Addis Ababa"}`,
          experience: experienceLevel || "Any",
          experienceLevel: experienceLevel, // Keep this for the modal
          experienceMin: job.experienceMin, // Keep for reference
          experienceMax: job.experienceMax, // Keep for reference
          postedAt: formatPostedDate(job.createdAt),
          description: job.description || t("requests.noDescription"),
          subcity: job.subcity || "Unknown",
          workTime: job.workTime,
          workState: job.workState || "pending",
          languages: job.languages || [],
          age_groups: job.age_groups || [],
          service_frequency: job.service_frequency,
          preferred_work_hours: job.preferred_work_hours,
          family_size: job.family_size,
          room_numbers: job.room_numbers,
          preferred_days: job.preferred_days || [],
          teaching_levels: job.teaching_levels || [],
          hours_per_day: job.hours_per_day,
          elderly_care_experience: job.elderly_care_experience,
          first_aid_cpr_certified: job.first_aid_cpr_certified,
          transportation_assistance: job.transportation_assistance,
          meal_preparation: job.meal_preparation,
          light_housekeeping: job.light_housekeeping,
          medication_management: job.medication_management,
          mobility_assistance: job.mobility_assistance,
          companionship: job.companionship,
          originalJob: job,
        };
      });
      setEnhancedJobs(enhanced);
    }
  }, [jobs, t]);

  // Handle Enter key press for search
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && mainSearch.trim()) {
      handleSearch();
    }
  };

  // Handle search button click
  const handleSearch = () => {
    if (mainSearch.trim()) {
      setSearchQuery(mainSearch);
      setCurrentPage(1);
    }
  };

  const toggleBookmark = (e, id) => {
    e.stopPropagation();
    const newBookmarks = new Set(bookmarkedJobs);
    if (newBookmarks.has(id)) newBookmarks.delete(id);
    else newBookmarks.add(id);
    setBookmarkedJobs(newBookmarks);
  };

  // Reset filters
  const resetFilters = () => {
    setMainSearch("");
    setSearchQuery("");
    setSelectedType("");
    setSelectedExperience("");
    setSubcityFilter("");
    setCurrentPage(1);
  };

  const handleApply = async (jobId) => {
    if (hasActiveWork) return;
    try {
      await applyToJob(jobId);
      getJobs({
        page: currentPage,
        limit: itemsPerPage,
        search: searchQuery,
        subcity: subcityFilter,
        workTime: selectedType,
        experienceLevel: selectedExperience,
      });
    } catch (error) {
      console.error("Error applying to job:", error);
    }
  };

  const handleViewDetails = (job) => {
    setSelectedJob(job.originalJob || job);
    setIsModalOpen(true);
  };

  const handleModalApply = (jobId) => {
    if (hasActiveWork) return;
    handleApply(jobId);
    setIsModalOpen(false);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setMainSearch("");
    setSearchQuery("");
    setCurrentPage(1);
  };

  const category = profileData?.category || "";
  const totalServiceRequests = jobPagination?.total || 0;
  const openServiceRequests = enhancedJobs.length;
  const CategoryIcon = categoryIcons[category] || Briefcase;

  const activeFiltersCount = [
    selectedType,
    selectedExperience,
    subcityFilter,
  ].filter(Boolean).length;

  // Translated work type label for active filter chip
  const getWorkTypeLabel = (value) => {
    const key = `requests.workTime${value
      .split("_")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join("")}`;
    return (
      t(key) || workTypeOptions.find((o) => o.value === value)?.label || value
    );
  };

  if (notOnboarded) {
    return <NotOnboardedMessage featureName="available service requests" />;
  }

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 pb-12 relative">
      {/* ACTIVE WORK WARNING */}
      {hasActiveWork && (
        <div className="mb-6 bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-900/30 rounded-2xl p-4">
          <div className="flex items-center gap-2">
            <AlertTriangle
              className="text-yellow-600 dark:text-yellow-400"
              size={20}
            />
            <p className="text-yellow-800 dark:text-yellow-300 font-semibold text-sm">
              {t("requests.noRequestsDesc")}
            </p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-text dark:text-dark-text">
            {t("requests.title")}
          </h1>
          <p className="text-gray-500 dark:text-dark-muted mt-1 text-sm sm:text-base">
            {t("requests.findNextOpp")}{" "}
            {category ? category.replace(/([A-Z])/g, " $1").toLowerCase() : ""}
          </p>
        </div>

        <div className="flex gap-3 sm:gap-4">
          <div className="bg-card dark:bg-dark-surface p-3 rounded-2xl shadow-sm border border-gray-100 dark:border-dark-border flex items-center gap-3 flex-1 sm:flex-none">
            <div className="text-primary">
              <CategoryIcon size={18} className="sm:w-5 sm:h-5" />
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-dark-muted">
                {t("requests.openRequests")}
              </p>
              <p className="text-lg font-bold text-text dark:text-dark-text">
                {openServiceRequests}
              </p>
            </div>
          </div>
          <div className="bg-card dark:bg-dark-surface p-3 rounded-2xl shadow-sm border border-gray-100 dark:border-dark-border flex items-center gap-3 flex-1 sm:flex-none">
            <div className="text-emerald-600 dark:text-emerald-400">
              <Briefcase size={18} className="sm:w-5 sm:h-5" />
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-dark-muted">
                {t("requests.totalRequests")}
              </p>
              <p className="text-lg font-bold text-text dark:text-dark-text">
                {totalServiceRequests}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {/* Search and Filter Toggle */}
        <div className="relative">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search
                className="absolute left-5 top-1/2 -translate-y-1/2 text-primary"
                size={20}
              />
              <input
                type="text"
                placeholder={t("requests.searchPlaceholder")}
                value={mainSearch}
                onChange={(e) => setMainSearch(e.target.value)}
                onKeyPress={handleKeyPress}
                className="w-full pl-14 pr-16 py-4 rounded-[1.5rem] bg-card dark:bg-dark-surface border border-gray-100 dark:border-dark-border shadow-sm text-lg outline-none focus:ring-2 focus:ring-primary/20 transition-all text-text dark:text-dark-text placeholder-gray-400 dark:placeholder-dark-muted"
              />
              {mainSearch ? (
                <div className="absolute right-4 top-1/2 -translate-y-1/2 flex gap-1">
                  <button
                    onClick={handleClearSearch}
                    className="text-gray-400 hover:text-gray-600 p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    title="Clear search"
                  >
                    <X size={20} />
                  </button>
                  <button
                    onClick={handleSearch}
                    className="bg-primary text-white p-2 rounded-xl hover:bg-primary/90 transition-colors"
                    title="Search"
                  >
                    <Search size={18} />
                  </button>
                </div>
              ) : (
                <div className="absolute right-4 top-1/2 -translate-y-1/2">
                  <Search className="text-gray-400" size={20} />
                </div>
              )}
            </div>

            {/* Filter Toggle Button */}
            <button
              onClick={() => setFiltersOpen(!filtersOpen)}
              className={`px-6 py-4 rounded-[1.5rem] border-2 font-semibold flex items-center justify-center gap-2 transition-all duration-200 shadow-sm ${
                filtersOpen
                  ? "bg-primary text-white border-primary shadow-primary/20"
                  : "bg-card dark:bg-dark-surface border-gray-100 dark:border-dark-border text-text dark:text-dark-text hover:border-primary/50 hover:bg-gray-50 dark:hover:bg-gray-800"
              }`}
            >
              <Filter size={20} />
              <span className="hidden sm:inline">{t("requests.filters")}</span>
              {activeFiltersCount > 0 && (
                <span className="ml-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center animate-in zoom-in duration-200">
                  {activeFiltersCount}
                </span>
              )}
              <ChevronDown
                size={18}
                className={`transition-transform duration-200 ${filtersOpen ? "rotate-180" : ""}`}
              />
            </button>
          </div>
        </div>

        {/* Collapsible Filter Bar */}
        {filtersOpen && (
          <div className="bg-card dark:bg-dark-surface rounded-2xl p-5 sm:p-6 border border-gray-100 dark:border-dark-border shadow-sm animate-in slide-in-from-top-2 duration-300">
            <div className="flex flex-col lg:flex-row gap-4 items-end">
              <div className="flex-1 w-full">
                <label className="text-xs font-bold text-gray-500 dark:text-dark-muted uppercase mb-2 block tracking-wider">
                  {t("requests.subcity")}
                </label>
                <div className="relative dropdown-container">
                  <button
                    type="button"
                    onClick={() => {
                      setShowSubcityDropdown(!showSubcityDropdown);
                      setShowWorkTypeDropdown(false);
                      setShowExperienceDropdown(false);
                    }}
                    className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-sm text-text dark:text-dark-text hover:bg-gray-100 dark:hover:bg-gray-800 transition-all"
                  >
                    <span>
                      {subcityFilter === ""
                        ? t("requests.allSubcities")
                        : subcityFilter}
                    </span>
                    <ChevronDown
                      size={16}
                      className={`text-primary transition-transform ${
                        showSubcityDropdown ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {showSubcityDropdown && (
                    <div className="absolute top-full left-0 right-0 mt-2 bg-card dark:bg-dark-surface rounded-xl shadow-xl border border-gray-100 dark:border-dark-border p-1 z-50 max-h-60 overflow-y-auto animate-in fade-in zoom-in-95 duration-200">
                      <button
                        onClick={() => {
                          setSubcityFilter("");
                          setCurrentPage(1);
                          setShowSubcityDropdown(false);
                        }}
                        className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                          subcityFilter === ""
                            ? "bg-primary text-white dark:bg-primary"
                            : "text-gray-600 dark:text-dark-muted hover:bg-accent dark:hover:bg-gray-800"
                        }`}
                      >
                        {t("requests.allSubcities")}
                      </button>
                      {SUBCITIES.map((subcity) => (
                        <button
                          key={subcity}
                          onClick={() => {
                            setSubcityFilter(subcity);
                            setCurrentPage(1);
                            setShowSubcityDropdown(false);
                          }}
                          className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                            subcityFilter === subcity
                              ? "bg-primary text-white dark:bg-primary"
                              : "text-gray-600 dark:text-dark-muted hover:bg-accent dark:hover:bg-gray-800"
                          }`}
                        >
                          {subcity}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex-1 w-full">
                <label className="text-xs font-bold text-gray-500 dark:text-dark-muted uppercase mb-2 block tracking-wider">
                  {t("requests.workType")}
                </label>
                <div className="relative dropdown-container">
                  <button
                    type="button"
                    onClick={() => {
                      setShowWorkTypeDropdown(!showWorkTypeDropdown);
                      setShowSubcityDropdown(false);
                      setShowExperienceDropdown(false);
                    }}
                    className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-sm text-text dark:text-dark-text hover:bg-gray-100 dark:hover:bg-gray-800 transition-all"
                  >
                    <span>
                      {selectedType === ""
                        ? t("requests.allWorkTypes")
                        : t(
                            `requests.workTime${selectedType
                              .split("_")
                              .map(
                                (w) => w.charAt(0).toUpperCase() + w.slice(1),
                              )
                              .join("")}`,
                          ) ||
                          workTypeOptions.find((o) => o.value === selectedType)
                            ?.label ||
                          selectedType}
                    </span>
                    <ChevronDown
                      size={16}
                      className={`text-primary transition-transform ${
                        showWorkTypeDropdown ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {showWorkTypeDropdown && (
                    <div className="absolute top-full left-0 right-0 mt-2 bg-card dark:bg-dark-surface rounded-xl shadow-xl border border-gray-100 dark:border-dark-border p-1 z-50 max-h-60 overflow-y-auto animate-in fade-in zoom-in-95 duration-200">
                      {workTypeOptions.map((type) => (
                        <button
                          key={type.value}
                          onClick={() => {
                            setSelectedType(type.value);
                            setCurrentPage(1);
                            setShowWorkTypeDropdown(false);
                          }}
                          className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                            selectedType === type.value
                              ? "bg-primary text-white dark:bg-primary"
                              : "text-gray-600 dark:text-dark-muted hover:bg-accent dark:hover:bg-gray-800"
                          }`}
                        >
                          {type.value === ""
                            ? t("requests.allWorkTypes")
                            : t(
                                `requests.workTime${type.value
                                  .split("_")
                                  .map(
                                    (w) =>
                                      w.charAt(0).toUpperCase() + w.slice(1),
                                  )
                                  .join("")}`,
                              ) || type.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex-1 w-full">
                <label className="text-xs font-bold text-gray-500 dark:text-dark-muted uppercase mb-2 block tracking-wider">
                  {t("requests.experienceLevel")}
                </label>
                <div className="relative dropdown-container">
                  <button
                    type="button"
                    onClick={() => {
                      setShowExperienceDropdown(!showExperienceDropdown);
                      setShowSubcityDropdown(false);
                      setShowWorkTypeDropdown(false);
                    }}
                    className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-sm text-text dark:text-dark-text hover:bg-gray-100 dark:hover:bg-gray-800 transition-all"
                  >
                    <span>
                      {selectedExperience === ""
                        ? t("requests.allExperience")
                        : experienceOptions.find(
                            (o) => o.value === selectedExperience,
                          )?.label || selectedExperience}
                    </span>
                    <ChevronDown
                      size={16}
                      className={`text-primary transition-transform ${
                        showExperienceDropdown ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {showExperienceDropdown && (
                    <div className="absolute top-full left-0 right-0 mt-2 bg-card dark:bg-dark-surface rounded-xl shadow-xl border border-gray-100 dark:border-dark-border p-1 z-50 max-h-60 overflow-y-auto animate-in fade-in zoom-in-95 duration-200">
                      {experienceOptions.map((exp) => (
                        <button
                          key={exp.value}
                          onClick={() => {
                            setSelectedExperience(exp.value);
                            setCurrentPage(1);
                            setShowExperienceDropdown(false);
                          }}
                          className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                            selectedExperience === exp.value
                              ? "bg-primary text-white dark:bg-primary"
                              : "text-gray-600 dark:text-dark-muted hover:bg-accent dark:hover:bg-gray-800"
                          }`}
                        >
                          {exp.value === ""
                            ? t("requests.allExperience")
                            : exp.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <button
                onClick={resetFilters}
                className="w-full lg:w-auto px-6 py-3 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors text-sm whitespace-nowrap"
              >
                <RotateCcw size={16} />
                {t("requests.resetAll")}
              </button>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
            <p className="mt-2 text-gray-500 dark:text-dark-muted">
              {t("requests.loading")}
            </p>
          </div>
        )}

        {/* Active Filter Chips */}
        {(searchQuery ||
          selectedType ||
          selectedExperience ||
          subcityFilter) && (
          <div className="flex flex-wrap gap-2 animate-in fade-in duration-300">
            {searchQuery && (
              <span className="px-3 py-1.5 bg-orange-500 text-white rounded-full text-sm font-medium flex items-center gap-1">
                &ldquo;{searchQuery}&rdquo;
                <button
                  onClick={() => {
                    setMainSearch("");
                    setSearchQuery("");
                  }}
                  className="ml-1 hover:text-orange-100 p-0.5 hover:bg-orange-600 rounded-full transition-colors"
                >
                  <X size={14} />
                </button>
              </span>
            )}
            {selectedType && (
              <span className="px-3 py-1.5 bg-blue-600 text-white rounded-full text-sm font-medium flex items-center gap-1">
                {getWorkTypeLabel(selectedType)}
                <button
                  onClick={() => setSelectedType("")}
                  className="ml-1 hover:text-blue-100 p-0.5 hover:bg-blue-700 rounded-full transition-colors"
                >
                  <X size={14} />
                </button>
              </span>
            )}
            {selectedExperience && (
              <span className="px-3 py-1.5 bg-purple-600 text-white rounded-full text-sm font-medium flex items-center gap-1">
                {
                  experienceOptions.find(
                    (opt) => opt.value === selectedExperience,
                  )?.label
                }
                <button
                  onClick={() => setSelectedExperience("")}
                  className="ml-1 hover:text-purple-100 p-0.5 hover:bg-purple-700 rounded-full transition-colors"
                >
                  <X size={14} />
                </button>
              </span>
            )}
            {subcityFilter && (
              <span className="px-3 py-1.5 bg-emerald-600 text-white rounded-full text-sm font-medium flex items-center gap-1">
                {subcityFilter}
                <button
                  onClick={() => setSubcityFilter("")}
                  className="ml-1 hover:text-emerald-100 p-0.5 hover:bg-emerald-700 rounded-full transition-colors"
                >
                  <X size={14} />
                </button>
              </span>
            )}
          </div>
        )}

        {/* Jobs Grid */}
        {!loading && enhancedJobs.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {enhancedJobs.map((job) => {
                const JobCategoryIcon =
                  categoryIcons[job.category] || Briefcase;
                const isJobAvailable =
                  job.workState === "pending" && !hasActiveWork;
                const isJobAssigned = job.workState !== "pending";

                return (
                  <div
                    key={job.id}
                    onClick={() => !hasActiveWork && handleViewDetails(job)}
                    className={`bg-card dark:bg-dark-surface rounded-3xl p-5 border border-gray-100 dark:border-dark-border shadow-sm transition-all duration-300 group flex flex-col h-full relative ${
                      hasActiveWork || isJobAssigned
                        ? "cursor-not-allowed opacity-80"
                        : "hover:shadow-md cursor-pointer hover:-translate-y-1"
                    }`}
                  >
                    {/* Overlay for locked/active-work state */}
                    {(hasActiveWork || isJobAssigned) && (
                      <div className="absolute inset-0 bg-white/50 dark:bg-black/30 backdrop-blur-[1px] rounded-3xl z-10 flex items-center justify-center">
                        <div className="text-center p-4">
                          {hasActiveWork ? (
                            <>
                              <AlertTriangle
                                className="text-yellow-600 mx-auto mb-2"
                                size={24}
                              />
                              <p className="text-sm font-semibold text-yellow-700 dark:text-yellow-400">
                                {t("requests.currentlyWorking")}
                              </p>
                            </>
                          ) : (
                            <>
                              <Lock
                                className="text-blue-600 mx-auto mb-2"
                                size={24}
                              />
                              <p className="text-sm font-semibold text-blue-700 dark:text-blue-400">
                                {getWorkStateLabel(job.workState)}
                              </p>
                            </>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Card Header */}
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className="p-2 rounded-xl shrink-0">
                          <JobCategoryIcon size={20} className="text-primary" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3
                            className="font-bold text-text dark:text-dark-text text-sm leading-tight line-clamp-2"
                            title={job.description}
                          >
                            {job.title}
                          </h3>
                          <span className="inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 text-xs font-medium">
                            {job.categoryLabel}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={(e) => toggleBookmark(e, job.id)}
                        className={`p-2 rounded-full transition-colors ml-2 shrink-0 ${
                          bookmarkedJobs.has(job.id)
                            ? "bg-pink-50 dark:bg-pink-900/20 text-pink-500"
                            : "text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-500 dark:hover:text-dark-text"
                        }`}
                        disabled={hasActiveWork}
                      >
                        <Bookmark
                          size={18}
                          fill={
                            bookmarkedJobs.has(job.id) ? "currentColor" : "none"
                          }
                        />
                      </button>
                    </div>

                    {/* Badges */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      <span className="px-2.5 py-1 rounded-lg bg-blue-600 text-white text-xs font-semibold">
                        {job.type}
                      </span>
                      <span className="px-2.5 py-1 rounded-lg bg-slate-600 text-white text-xs font-semibold">
                        {job.experience}{" "}
                        {job.experience !== "Any" && t("requests.years")}
                      </span>
                      {job.languages && job.languages.length > 0 && (
                        <span className="px-2.5 py-1 rounded-lg bg-emerald-600 text-white text-xs font-semibold">
                          {job.languages[0]}
                          {job.languages.length > 1 &&
                            ` +${job.languages.length - 1}`}
                        </span>
                      )}
                      {isJobAssigned && (
                        <span
                          className={`px-2.5 py-1 rounded-lg text-xs font-semibold border ${getWorkStateColor(job.workState)}`}
                        >
                          {getWorkStateLabel(job.workState)}
                        </span>
                      )}
                    </div>

                    {/* Meta info */}
                    <div className="space-y-2 mb-6 flex-1">
                      <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-dark-muted">
                        <MapPin size={14} className="text-primary shrink-0" />
                        <span className="truncate">{job.location}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-dark-muted">
                        <Clock size={14} className="text-primary shrink-0" />
                        <span>
                          {t("requests.postedOn")} {job.postedAt}
                        </span>
                      </div>
                    </div>

                    {/* Apply Button */}
                    <div className="pt-4 border-t border-gray-100 dark:border-gray-700 mt-auto">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleApply(job.id);
                        }}
                        disabled={!isJobAvailable}
                        className={`w-full py-2.5 text-white rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2 duration-200 shadow-lg shadow-primary/20 dark:shadow-none ${
                          !isJobAvailable
                            ? "bg-gray-300 text-gray-500 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500"
                            : "bg-primary hover:bg-primary/90 hover:scale-[1.02] active:scale-[0.98]"
                        }`}
                      >
                        {!isJobAvailable ? (
                          isJobAssigned ? (
                            getWorkStateLabel(job.workState)
                          ) : (
                            t("requests.currentlyWorking")
                          )
                        ) : (
                          <>
                            {t("requests.applyNow")} <ArrowRight size={16} />
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            <div className="mt-8">
              <Pagination
                currentPage={currentPage}
                totalPages={jobPagination?.totalPages || 1}
                onPageChange={handlePageChange}
                itemsPerPage={itemsPerPage}
                onItemsPerPageChange={handleItemsPerPageChange}
                totalItems={jobPagination?.total || 0}
              />
            </div>
          </>
        ) : (
          !loading && (
            <div className="bg-card dark:bg-dark-surface rounded-[2rem] p-8 sm:p-12 text-center border border-gray-100 dark:border-dark-border">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gray-100 dark:bg-gray-900 rounded-full flex items-center justify-center mx-auto mb-6 text-gray-400 dark:text-gray-600">
                <Search size={24} className="sm:w-8 sm:h-8" />
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-text dark:text-dark-text mb-2">
                {t("requests.noRequests")}
              </h3>
              <p className="text-gray-500 dark:text-dark-muted mb-6 text-sm sm:text-base">
                {t("requests.noRequestsDesc")}
              </p>
              <button
                onClick={resetFilters}
                className="px-6 py-3 bg-primary hover:bg-primary/90 text-white rounded-xl font-bold transition-colors shadow-lg shadow-primary/20 dark:shadow-none text-sm sm:text-base"
              >
                {t("requests.resetAll")}
              </button>
            </div>
          )
        )}
      </div>

      {/* Job Detail Modal */}
      {isModalOpen && selectedJob && (
        <JobDetailModal
          job={selectedJob}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onApply={handleModalApply}
          hasActiveWork={hasActiveWork}
        />
      )}
    </div>
  );
};

export default CaregiverRequestList;
